import { TuyauError } from '@tuyau/core/client'
import { AxiosError } from 'axios'
import { toast } from 'sonner'
import type { PermissionTuple } from '~/utils/ability'
import { describePermission } from '~/utils/permissionLabels'

/** Corps d'un 403 `RbacForbiddenException` du backend. */
interface RbacForbiddenBody {
  code: string
  missingPermissions: { action: string; resource: string }[]
}

/** Corps de la réponse 403, que l'appel vienne du client legacy (axios) ou de Tuyau (ky). */
const extractForbiddenBody = (error: unknown): unknown => {
  if (error instanceof AxiosError && error.response?.status === 403) return error.response.data
  if (error instanceof TuyauError && error.isStatus(403)) return error.response
  return null
}

/** Tuples manquants si l'erreur est un refus RBAC (`E_RBAC_PERMISSION_DENIED`), sinon `null`. */
export const getRbacMissingPermissions = (error: unknown): PermissionTuple[] | null => {
  const body = extractForbiddenBody(error) as Partial<RbacForbiddenBody> | null
  if (body?.code !== 'E_RBAC_PERMISSION_DENIED' || !Array.isArray(body.missingPermissions)) {
    return null
  }

  // Les tuples viennent de la taxonomie côté backend ; `describePermission` retombe de toute façon
  // sur les libellés bruts pour un couple inconnu.
  return body.missingPermissions.map(
    ({ action, resource }) => [action, resource] as unknown as PermissionTuple,
  )
}

/**
 * Toast de refus RBAC, avec la liste des droits exacts à obtenir. L'`id` sonner est dérivé des
 * tuples manquants : plusieurs requêtes refusées pour les mêmes droits (typique au chargement d'un
 * écran) ne produisent qu'un seul toast.
 */
export const toastRbacForbidden = (
  missingPermissions: PermissionTuple[],
  title = 'Droits insuffisants pour charger certaines informations',
) => {
  const described = missingPermissions.map(describePermission)
  const id = `rbac-forbidden:${described
    .map((permission) => permission.key)
    .sort()
    .join(',')}`

  toast.error(title, {
    id,
    description: (
      <ul className="flex flex-col gap-0.5">
        {described.map((permission) => (
          <li key={permission.key}>
            <span className="font-medium">{permission.actionLabel}</span>
            {' — '}
            {permission.segments.join(' › ')}
          </li>
        ))}
      </ul>
    ),
  })
}
